import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangleIcon } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { z } from "zod";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import Logo from "../../components/Logo";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { useReset2FAMutation } from "../../services/api";

export default function Reset2FA() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();
  const [reset2FA] = useReset2FAMutation();

  const schema = z.object({
    email: z
      .string()
      .min(1, t("reset-2fa.required-email"))
      .email(t("reset-2fa.invalid-email")),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: Values) {
    try {
      await reset2FA({ email: values.email }).unwrap();
      showNotification("success", t("reset-2fa.email-sent"));
      navigate("/login");
    } catch {
      showNotification("error", t("reset-2fa.email-not-sent"));
    }
  }

  return (
    <>
      <Helmet>
        <title>{t("reset-2fa.title")} | Betterfield</title>
      </Helmet>
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <Form {...form}>
          <form
            autoComplete="off"
            noValidate
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <div className="space-y-2">
              <h1 className="text-2xl font-bold tracking-tight">
                {t("reset-2fa.title")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("reset-2fa.subtitle")}
              </p>
            </div>

            <Alert variant="warning">
              <AlertTriangleIcon />
              <AlertDescription>{t("reset-2fa.warning")}</AlertDescription>
            </Alert>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("reset-2fa.email-label")}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="username"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col items-center gap-3">
              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                {t("reset-2fa.send-button")}
              </Button>
              <Button asChild variant="link" size="sm">
                <RouterLink to="/login">
                  {t("reset-2fa.back-to-login")}
                </RouterLink>
              </Button>
            </div>
          </form>
        </Form>
      </main>
    </>
  );
}
